import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";

export type GanttRow = {
  contextOnly: boolean;
  depth: number;
  end: string | null;
  id: string;
  kind: "goal" | "work_item";
  label: string;
  percent?: number;
  source: "due_date" | "explicit" | "none" | "sprint";
  start: string | null;
};

const ROW_HEIGHT = 32;
const HEADER_HEIGHT = 28;
const WIDTH = 720;
const MARGIN = { bottom: 8, left: 12, right: 12, top: HEADER_HEIGHT };
const PLOT_WIDTH = WIDTH - MARGIN.left - MARGIN.right;

// Same "visible window over a longer domain" idiom as burndown-chart.tsx,
// but the domain is a continuous day-number range (a Gantt spans months,
// not a fixed list of discrete days) rather than an array index.
const DEFAULT_WINDOW_DAYS = 180; // ~6 months
const MIN_WINDOW_DAYS = 60; // ~2 months

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const toDayNumber = (date: string): number => {
  const [year, month, day] = date.split("-").map(Number);
  return Math.floor(Date.UTC(year, month - 1, day) / 86_400_000);
};
const fromDayNumber = (day: number): { month: number; year: number } => {
  const date = new Date(day * 86_400_000);
  return { month: date.getUTCMonth(), year: date.getUTCFullYear() };
};

const clampWindow = (start: number, size: number, domainStart: number, domainEnd: number) => {
  const domainSpan = Math.max(1, domainEnd - domainStart);
  const clampedSize = Math.min(domainSpan, Math.max(MIN_WINDOW_DAYS, size));
  const clampedStart = Math.min(Math.max(domainStart, start), domainEnd - clampedSize);
  return { size: clampedSize, start: clampedStart };
};

/**
 * Hand-rolled inline SVG Gantt canvas -- same "no chart library" posture as
 * burndown-chart.tsx, adapted from a per-day index axis to a continuous
 * month axis. The SVG itself is decorative (aria-hidden): row labels and
 * each row's start/end/percent are real HTML text in the two flanking
 * columns, so nothing here depends on parsing bar geometry to be usable
 * with a screen reader or keyboard, per WCAG 2.1 AA.
 */
export function GanttChart({ rows }: { rows: GanttRow[] }) {
  const todayDay = useMemo(() => toDayNumber(new Date().toISOString().slice(0, 10)), []);
  const domain = useMemo(() => {
    const days = rows
      .filter((row) => row.start && row.end)
      .flatMap((row) => [toDayNumber(row.start!), toDayNumber(row.end!)]);
    days.push(todayDay);
    const start = Math.min(...days);
    const end = Math.max(...days, start + 30); // guarantee a minimum span even with only single-day milestones
    return { end, start };
  }, [rows, todayDay]);

  const [windowSize, setWindowSize] = useState(() => Math.min(domain.end - domain.start, DEFAULT_WINDOW_DAYS));
  const [windowStart, setWindowStart] = useState(() => clampWindow(todayDay - 30, windowSize, domain.start, domain.end).start);
  const dragRef = useRef<{ startClientX: number; startWindowStart: number } | null>(null);

  useEffect(() => {
    const size = Math.min(domain.end - domain.start, DEFAULT_WINDOW_DAYS);
    setWindowSize(size);
    setWindowStart(clampWindow(todayDay - 30, size, domain.start, domain.end).start);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain.start, domain.end]);

  const { size: clampedSize, start: clampedStart } = clampWindow(windowStart, windowSize, domain.start, domain.end);
  const isZoomed = clampedSize < domain.end - domain.start;

  const xForDay = (day: number) => MARGIN.left + ((day - clampedStart) / clampedSize) * PLOT_WIDTH;

  const monthTicks = useMemo(() => {
    const ticks: Array<{ day: number; label: string }> = [];
    const first = fromDayNumber(clampedStart);
    let cursor = Math.floor(Date.UTC(first.year, first.month, 1) / 86_400_000);
    // A real calendar month's length varies, so step by re-deriving the
    // next month boundary from the calendar rather than a fixed day count.
    for (let guard = 0; guard < 36 && cursor < clampedStart + clampedSize; guard += 1) {
      if (cursor >= clampedStart - 31) {
        const { month, year } = fromDayNumber(cursor);
        ticks.push({ day: cursor, label: `${MONTH_NAMES[month]} ${year}` });
      }
      const { month, year } = fromDayNumber(cursor);
      cursor = Math.floor(Date.UTC(month === 11 ? year + 1 : year, (month + 1) % 12, 1) / 86_400_000);
    }
    return ticks;
  }, [clampedStart, clampedSize]);

  const height = MARGIN.top + rows.length * ROW_HEIGHT + MARGIN.bottom;

  const zoomIn = () => setWindowSize((size) => clampWindow(clampedStart, Math.max(MIN_WINDOW_DAYS, Math.round(size * 0.75)), domain.start, domain.end).size);
  const zoomOut = () => setWindowSize((size) => clampWindow(clampedStart, Math.round(size / 0.75), domain.start, domain.end).size);
  const resetZoom = () => { setWindowSize(domain.end - domain.start); setWindowStart(domain.start); };
  const panBy = (deltaDays: number) => setWindowStart((start) => clampWindow(start + deltaDays, clampedSize, domain.start, domain.end).start);
  const stepSize = () => Math.max(7, Math.floor(clampedSize / 4));

  const onPointerDown = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (!isZoomed) return;
    dragRef.current = { startClientX: event.clientX, startWindowStart: clampedStart };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const onPointerMove = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (!dragRef.current) return;
    const svgRect = event.currentTarget.getBoundingClientRect();
    const pixelsPerDay = (PLOT_WIDTH * (svgRect.width / WIDTH)) / Math.max(1, clampedSize);
    const deltaDays = Math.round((dragRef.current.startClientX - event.clientX) / Math.max(1, pixelsPerDay));
    setWindowStart(clampWindow(dragRef.current.startWindowStart + deltaDays, clampedSize, domain.start, domain.end).start);
  };
  const onPointerUp = () => { dragRef.current = null; };

  return (
    <div className="gantt-chart">
      <div className="gantt-zoom-controls">
        <button aria-label="Zoom out" disabled={!isZoomed} onClick={zoomOut} type="button">
          <i aria-hidden="true" className="bi bi-dash-lg" />
        </button>
        <button aria-label="Zoom in" disabled={clampedSize <= MIN_WINDOW_DAYS} onClick={zoomIn} type="button">
          <i aria-hidden="true" className="bi bi-plus-lg" />
        </button>
        <button aria-label="Pan earlier" disabled={clampedStart <= domain.start} onClick={() => panBy(-stepSize())} type="button">
          <i aria-hidden="true" className="bi bi-chevron-left" />
        </button>
        <button aria-label="Pan later" disabled={clampedStart + clampedSize >= domain.end} onClick={() => panBy(stepSize())} type="button">
          <i aria-hidden="true" className="bi bi-chevron-right" />
        </button>
        <button disabled={!isZoomed} onClick={resetZoom} type="button">Reset zoom</button>
      </div>
      <div className="gantt-grid">
        <div className="gantt-row-labels">
          <div className="gantt-header-spacer" />
          {rows.map((row) => (
            <div className={`gantt-row-label${row.contextOnly ? " context-only" : ""}`} key={row.id} style={{ height: ROW_HEIGHT, paddingLeft: Math.min(row.depth, 6) * 16 }}>
              {row.kind === "goal" ? <strong>{row.label}</strong> : <span>{row.label}</span>}
            </div>
          ))}
        </div>
        <svg
          aria-hidden="true"
          height={height}
          onPointerDown={onPointerDown}
          onPointerLeave={onPointerUp}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          style={isZoomed ? { cursor: dragRef.current ? "grabbing" : "grab" } : undefined}
          viewBox={`0 0 ${WIDTH} ${height}`}
        >
          {monthTicks.map((tick) => (
            <g key={tick.day}>
              <line className="gantt-gridline" x1={xForDay(tick.day)} x2={xForDay(tick.day)} y1={0} y2={height} />
              <text className="gantt-axis-label" x={xForDay(tick.day) + 4} y={18}>{tick.label}</text>
            </g>
          ))}
          {todayDay >= clampedStart && todayDay <= clampedStart + clampedSize && (
            <line className="gantt-today-line" x1={xForDay(todayDay)} x2={xForDay(todayDay)} y1={0} y2={height} />
          )}
          {rows.map((row, index) => {
            const y = MARGIN.top + index * ROW_HEIGHT;
            if (row.kind === "goal" || !row.start || !row.end) return null;
            const x1 = xForDay(toDayNumber(row.start));
            const x2 = Math.max(x1 + 6, xForDay(toDayNumber(row.end) + 1));
            const barClass = `gantt-bar gantt-bar-${row.source}${row.contextOnly ? " context-only" : ""}`;
            if (row.source === "due_date") {
              const cx = (x1 + x2) / 2;
              const cy = y + ROW_HEIGHT / 2;
              return <path className={barClass} d={`M${cx},${cy - 7} L${cx + 7},${cy} L${cx},${cy + 7} L${cx - 7},${cy} Z`} key={row.id} />;
            }
            return <rect className={barClass} height={ROW_HEIGHT - 12} key={row.id} rx={3} width={x2 - x1} x={x1} y={y + 6} />;
          })}
        </svg>
        <div className="gantt-row-info">
          <div className="gantt-header-spacer" />
          {rows.map((row) => (
            <div className="gantt-row-info-cell" key={row.id} style={{ height: ROW_HEIGHT }}>
              {row.kind === "goal" && row.percent !== undefined ? (
                <span>{row.percent}% complete</span>
              ) : row.start && row.end ? (
                <span>
                  {row.start === row.end ? row.start : `${row.start} → ${row.end}`}
                  {row.source === "sprint" && " (sprint)"}
                </span>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
